// Places and departure times for the website. Copied from the Cloud
// Functions (functions/places.js and functions/departure.js), which mirror
// Places.swift and DateUtils.departureDate on iOS. Keep all three in sync.

/* ── Departure times (functions/departure.js) ── */

// Ride departure times, resolved the same way as DateUtils.departureDate on iOS.
//
// iOS/Android store `date` as the true UTC instant of departure; the website
// stores local wall-clock time with a "Z" tacked on. We tell them apart by
// checking whether the instant, shown in local time, matches the separately
// stored `time` ("h:mm a"). Users are in Colorado, so "local" is Mountain Time.

const TZ = 'America/Denver';

const timeFmt = new Intl.DateTimeFormat('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true });
const dayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
const labelFmt = new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric' });

/** "2:30 PM" / "14:30" → { h, m } in 24-hour time, or null. */
function parseTime(text) {
  const m = String(text || '').trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (m[3]) {
    const pm = m[3].toUpperCase() === 'PM';
    if (h === 12) h = pm ? 12 : 0;
    else if (pm) h += 12;
  }
  return h < 24 && min < 60 ? { h, m: min } : null;
}

/** Normalised "h:mm AM/PM" for comparing against the stored time. */
function formatTime(date) {
  return timeFmt.format(date).replace(/ /g, ' ');
}

/** The UTC instant for a Mountain-Time wall clock (yyyy-mm-dd + hh:mm). */
function mountainToUtc(ymd, h, m) {
  const [y, mo, d] = ymd.split('-').map(Number);
  // Start from the wall time as if it were UTC, then correct by the zone
  // offset at that moment (twice, to settle across DST changes).
  let guess = Date.UTC(y, mo - 1, d, h, m);
  for (let i = 0; i < 2; i++) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
      timeZone: TZ, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    }).formatToParts(new Date(guess)).map((p) => [p.type, p.value]));
    const shown = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute);
    guess += Date.UTC(y, mo - 1, d, h, m) - shown;
  }
  return new Date(guess);
}

/** When the ride actually leaves, or null if the stored values can't be read. */
function departure(ride) {
  const dateStr = String((ride && ride.date) || '');
  const t = parseTime(ride && ride.time);
  const instant = new Date(dateStr);
  if (!Number.isNaN(instant.getTime()) && t) {
    const shown = parseTime(formatTime(instant));
    if (shown && shown.h === t.h && shown.m === t.m) return instant;
  }
  const ymd = dateStr.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  if (!t) return Number.isNaN(instant.getTime()) ? null : instant;
  return mountainToUtc(ymd, t.h, t.m);
}

/** Mountain-Time calendar day, "2026-11-14". */
function localDay(date) {
  return dayFmt.format(date);
}




/** "Sat, Nov 14" — day only. */
function describeDay(ride) {
  const dep = departure(ride);
  return dep ? labelFmt.format(dep) : '';
}

/* ── Places (functions/places.js) ── */

const COMMON = [
  'USAFA', 'Denver Airport (DEN)', 'COS Airport', 'Vandy ECP', 'Sijan ECP',
  'USAFA Up Top', 'Downtown Denver', 'Colorado Springs', 'Breckenridge', 'Keystone',
];

const ALIASES = {
  'Denver Airport (DEN)': ['den', 'dia', 'kden', 'denver airport', 'den airport', 'denver international',
    'denver international airport', 'denver intl airport', 'denver intl. airport'],
  'COS Airport': ['cos', 'kcos', 'cos airport', 'colorado springs airport'],
  'USAFA': ['usafa', 'academy', 'air force academy', 'usaf academy'],
  'USAFA Up Top': ['usafa up top', 'usafa uptop', 'up top'],
  'Downtown Denver': ['downtown denver'],
  'Colorado Springs': ['colorado springs', 'co springs', 'csprings', 'the springs', 'downtown springs'],
  'Vandy ECP': ['vandy ecp', 'usafa vandy ecp', 'vandenberg ecp'],
  'Sijan ECP': ['sijan ecp', 'sijan'],
  'Breckenridge': ['breckenridge', 'breck'],
  'Keystone': ['keystone'],
};

// A broad place also covers its specific spots: an alert for "USAFA" fires
// for rides at Up Top and the gates. Asking for a specific spot stays exact.
const GROUPS = { USAFA: ['USAFA', 'USAFA Up Top', 'Vandy ECP', 'Sijan ECP'] };

// Vague names that could mean more than one known place.
const BROAD = { denver: ['Denver Airport (DEN)', 'Downtown Denver'] };

/** Lowercase, strip accents and "(uber)"-style notes, collapse punctuation/spaces. */
function normalize(text) {
  return String(text || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^a-z0-9 ]/g, ' ')
    .split(' ').filter(Boolean).join(' ');
}

function canonical(text) {
  const n = normalize(text);
  const exact = COMMON.find((p) => normalize(p) === n);
  if (exact) return exact;
  for (const [place, names] of Object.entries(ALIASES)) {
    if (names.some((a) => normalize(a) === n)) return place;
  }
  return null;
}

/** Whether a ride's location satisfies what an alert asked for. */
function matches(alert, location) {
  const a = canonical(alert);
  const broad = BROAD[normalize(location)];
  if (a && broad) return broad.includes(a);
  const l = canonical(location);
  if (a && l) return a === l;
  const na = normalize(alert);
  const nl = normalize(location);
  return na.length > 0 && (nl === na || nl.includes(na));
}

// Short names for notifications, so "dia" and "Denver Airport (DEN)" both read DEN.
const SHORT = { 'Denver Airport (DEN)': 'DEN', 'COS Airport': 'COS' };

/** A typed location tidied for display: known places by their short name,
 *  all-lowercase text capitalised ("denver" → "Denver"), anything else as typed. */
function displayPlace(text) {
  const raw = String(text || '').trim().replace(/\s+/g, ' ');
  if (!raw) return '';
  const known = canonical(raw);
  if (known) return SHORT[known] || known;
  if (raw === raw.toLowerCase()) return raw.replace(/\b[a-z]/g, (c) => c.toUpperCase());
  return raw;
}

/** Like matches(), but a broad place (see GROUPS) also accepts its spots. */
function matchesPlace(wanted, location) {
  return (GROUPS[canonical(wanted)] || [wanted]).some((p) => matches(p, location));
}

/**
 * When the alert wants a specific day ("2026-11-14", Mountain Time) and
 * optionally a time with a window (time "12:00 PM", windowHours 2), check the
 * ride leaves inside it. Alerts without a date match any upcoming ride.
 */
function matchesWhen(alert, ride) {
  if (!alert.date) return true;
  const dep = departure(ride);
  if (!dep) return false;
  if (!alert.time) return localDay(dep) === alert.date;
  const t = parseTime(alert.time);
  if (!t) return localDay(dep) === alert.date;
  const wanted = mountainToUtc(alert.date, t.h, t.m);
  const windowMs = (Number(alert.windowHours) || 2) * 60 * 60 * 1000;
  return Math.abs(dep.getTime() - wanted.getTime()) <= windowMs;
}

/** How far apart a ride and a request can leave and still count as a match. */
const REQUEST_WINDOW_MS = 3 * 60 * 60 * 1000;

/** Two posts name the same place, either way round: a rider at Vandy ECP and a driver leaving USAFA can meet. */
function samePlace(a, b) {
  return matchesPlace(a, b) || matchesPlace(b, a);
}

/**
 * A ride request matches a newly posted ride when both ends name the same
 * place (USAFA and its spots count as one, either way round) and they leave
 * on the same Mountain-Time day within 3 hours of each other.
 */
function requestMatchesRide(request, ride) {
  if (!request || !ride || !request.toLocation || !request.fromLocation) return false;
  if (!samePlace(request.toLocation, ride.toLocation)) return false;
  if (!samePlace(request.fromLocation, ride.fromLocation)) return false;
  const wanted = departure(request);
  const leaves = departure(ride);
  if (!wanted || !leaves || localDay(wanted) !== localDay(leaves)) return false;
  return Math.abs(wanted.getTime() - leaves.getTime()) <= REQUEST_WINDOW_MS;
}

/** An alert matches a ride if destination, origin (when set) and date/time window (when set) all match. */
function alertMatchesRide(alert, ride) {
  if (!alert || !alert.to || !ride) return false;
  if (!matchesPlace(alert.to, ride.toLocation)) return false;
  if (alert.from && !matchesPlace(alert.from, ride.fromLocation)) return false;
  return matchesWhen(alert, ride);
}

export {
  COMMON, SHORT, departure, localDay, parseTime, mountainToUtc, describeDay,
  normalize, canonical, displayPlace, matches, matchesPlace, alertMatchesRide, requestMatchesRide,
};
